import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Terminal, Home, Mail, User, Code, Layers, Zap } from 'lucide-react';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  targetSection: string;
  command: string;
}

const StickyVerticalMenu: React.FC = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [isVisible, setIsVisible] = useState(false);
  const [menuExpanded, setMenuExpanded] = useState(false);
  const [glitchActive, setGlitchActive] = useState(false);

  const menuItems: MenuItem[] = useMemo(() => [
    {
      id: 'hero',
      label: 'Home',
      icon: <Home size={18} />,
      targetSection: 'hero-section',
      command: './home.sh'
    },
    {
      id: 'about',
      label: 'About',
      icon: <User size={18} />,
      targetSection: 'terminal-section',
      command: './about.sh'
    },
    {
      id: 'services',
      label: 'Services',
      icon: <Code size={18} />,
      targetSection: 'tech-showcase',
      command: './services.sh'
    },    {
      id: 'team',
      label: 'DevTeam',
      icon: <Layers size={18} />,
      targetSection: 'team-members-section',
      command: './team.sh'
    },
    {
      id: 'contact',
      label: 'Contact',
      icon: <Mail size={18} />,
      targetSection: 'footer',
      command: './contact.sh'
    }
  ], []);

  // Smooth scroll with parallax effect
  const scrollToSection = useCallback((targetId: string) => {
    const element = document.querySelector(`.${targetId}`) || 
                   document.getElementById(targetId) ||
                   document.querySelector(`[data-section="${targetId}"]`);
    
    if (element) {
      const headerHeight = 100; // Account for header
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerHeight;

      // Trigger glitch effect
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 300);

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  // Track active section based on scroll position
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollPosition = window.scrollY + 200;
          
          // Show menu after scrolling past hero
          setIsVisible(scrollPosition > 100);          // Determine active section with priority order (most specific first)
          const sectionPriority = [
            { class: 'hero-section', id: 'hero' },
            { class: 'tech-showcase', id: 'services' },  // Check tech-showcase first
            { class: 'terminal-section', id: 'about' },
            { class: 'team-members-section', id: 'team' },
            { class: 'footer', id: 'contact' }
          ];
          
          let foundActiveSection = null;
          
          for (const section of sectionPriority) {
            const element = document.querySelector(`.${section.class}`) || 
                           document.getElementById(section.class);
            if (element) {
              const rect = element.getBoundingClientRect();
              const elementTop = rect.top + window.pageYOffset;
              const elementBottom = elementTop + rect.height;
              
              // For tech-showcase, use more precise detection
              const threshold = section.class === 'tech-showcase' ? 150 : 300;
              
              if (scrollPosition >= elementTop - threshold && scrollPosition < elementBottom - threshold) {
                foundActiveSection = section.id;
                break;
              }
            }
          }
          
          if (foundActiveSection) {
            setActiveSection(foundActiveSection);
          }
          
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Random glitch effect
  useEffect(() => {
    const glitchInterval = setInterval(() => {
      if (Math.random() < 0.05) { // 5% chance
        setGlitchActive(true);
        setTimeout(() => setGlitchActive(false), 150);
      }
    }, 3000);

    return () => clearInterval(glitchInterval);
  }, []);

  return (
    <div className={`sticky-vertical-menu ${isVisible ? 'visible' : ''} ${glitchActive ? 'glitch' : ''}`}>
      <div className="menu-container">
        {/* Terminal Header */}
        <div className="menu-header">
          <div className="terminal-dots">
            <div className="dot red"></div>
            <div className="dot yellow"></div>
            <div className="dot green"></div>
          </div>
          <div className="menu-title">
            <Terminal size={16} />
            <span>NAV</span>
          </div>
        </div>

        {/* Menu Items */}
        <div className="menu-items">
          {menuItems.map((item, index) => (
            <div
              key={item.id}
              className={`menu-item ${activeSection === item.id ? 'active' : ''}`}
              onClick={() => scrollToSection(item.targetSection)}
              onMouseEnter={() => setMenuExpanded(true)}
              onMouseLeave={() => setMenuExpanded(false)}
              style={{
                animationDelay: `${index * 100}ms`
              }}
            >
              <div className="menu-item-content">
                <div className="menu-icon">
                  {item.icon}
                </div>
                <div className={`menu-label ${menuExpanded ? 'expanded' : ''}`}>
                  <span className="menu-text">{item.label}</span>
                  <span className="menu-command">{item.command}</span>
                </div>
              </div>
              <div className="menu-item-bg"></div>
              <div className="menu-item-glow"></div>
            </div>
          ))}
        </div>        {/* Terminal Footer */}
        <div className="menu-footer">
          <div className="status-indicator">
            <Zap size={12} />
            <span>{menuExpanded ? 'ONLINE' : 'OFFLINE'}</span>
          </div>
        </div>

        {/* Holographic overlay */}
        <div className="holographic-overlay"></div>
      </div>
    </div>
  );
};

export default StickyVerticalMenu;
